import React from 'react';
import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import type { PortfolioSite } from '../../redux/api/portfolioApi';

interface SiteSelectorProps {
  currentSite: PortfolioSite | null;
  portfolioSites: PortfolioSite[];
  sitesLoading: boolean;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (open: boolean) => void;
  siteSearchTerm: string;
  setSiteSearchTerm: (term: string) => void;
  dropdownPosition: { top: number; left: number; width: number } | null;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  siteSelectorButtonRef: React.RefObject<HTMLButtonElement | null>;
  siteDropdownRef: React.RefObject<HTMLDivElement | null>;
  handleSiteSelect: (site: PortfolioSite) => Promise<void>;
}

const SiteSelector: React.FC<SiteSelectorProps> = ({
  currentSite,
  portfolioSites,
  sitesLoading,
  isDropdownOpen,
  setIsDropdownOpen,
  siteSearchTerm,
  setSiteSearchTerm,
  dropdownPosition,
  dropdownRef,
  siteSelectorButtonRef,
  siteDropdownRef,
  handleSiteSelect,
}) => {
  const filteredSites = portfolioSites.filter(site =>
    siteSearchTerm === '' ||
    site.site_name.toLowerCase().includes(siteSearchTerm.toLowerCase()) ||
    site.site_code?.toLowerCase().includes(siteSearchTerm.toLowerCase())
  );

  return (
    <div className="relative group" ref={dropdownRef}>
      <button
        ref={siteSelectorButtonRef}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="eg-input w-full pr-8 text-left truncate hover:border-engramiq-green/40"
        disabled={sitesLoading || portfolioSites.length === 0}
      >
        {currentSite?.site_name
          ? currentSite.site_name
          : sitesLoading
            ? 'Loading sites...'
            : portfolioSites.length === 0
              ? 'No sites available'
              : 'Select site'}
      </button>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-engramiq-green pointer-events-none z-10" />

      {isDropdownOpen && dropdownPosition && createPortal(
        <div
          ref={siteDropdownRef}
          className="fixed eg-dropdown z-[9999]"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
        >
          <div className="p-2 eg-divider">
            <input
              type="text"
              placeholder="Search sites..."
              value={siteSearchTerm}
              onChange={(e) => setSiteSearchTerm(e.target.value)}
              className="eg-input w-full"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto">
            {filteredSites.map(site => (
              <button
                key={site.site_id}
                onClick={() => handleSiteSelect(site)}
                className={`eg-dropdown-item ${currentSite?.site_id === site.site_id ? 'eg-dropdown-item-selected' : ''}`}
              >
                <div className="font-medium">{site.site_name}</div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SiteSelector;
